// Sentry needs to be initialized as soon as possible to catch errors that may be thrown during app init
import { SentryRoutes, useLogging } from "./logging/logging";

// The store need to be imported before any other components that may use it
import { store } from "./store/store";

import { FileUploadContextProvider } from "@/components/common/file-upload-context/file-uploads-context";
import { PickersLocalizationProvider } from "@/components/common/pickers-localization-provider";
import { ViewRuntimeContextProvider } from "@/components/common/view-runtime-context";
import {
  ErrorHandlingContext,
  useErrorHandlers,
} from "@/errors/components/error-handling-context";
import { SavedViewContextProvider } from "@/features/saved-views/saved-view-context";
import { ShareLinkPage } from "@/features/saved-views/share-link-page";
import { HomeRedirect } from "@/pages/home-redirect";
import { NotFoundPage } from "@/pages/not-found";
import { ProjectPage } from "@/pages/project";
import {
  WEB_EDITOR_ROUTE,
  WebEditorRedirectPage,
} from "@/pages/web-editor-redirect-page";
import { runtimeConfig } from "@/runtime-config";
import { parseFeaturesAndValuesFromUrl } from "@/store/features/features";
import {
  disableFeature,
  enableFeature,
  initFeatures,
} from "@/store/features/features-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { APP_VERSION } from "@/utils/env-constants";
import { HandleWindowClose } from "@/utils/handle-window-close";
import {
  WaypointContextProvider,
  extendR3FWithLotVComponents,
} from "@faro-lotv/app-component-toolbox";
import { DialogProvider, ToastProvider } from "@faro-lotv/flat-ui";
import {
  HBCookieManagerProvider,
  IntercomProviderWithCookieIntegration,
  LocalizeProvider,
} from "@faro-lotv/foreign-observers";
import { AuthProvider } from "@faro-lotv/gate-keepers";
import { initLotvWorkers } from "@faro-lotv/lotv";
import { CoreApiError, useCoreApiClient } from "@faro-lotv/service-wires";
import GltfLoading from "@faro-lotv/web-workers/dist/GltfLoading.worker.js?url";
import ImageDataLoading from "@faro-lotv/web-workers/dist/ImageDataLoading.worker.js?url";
import Orthophoto from "@faro-lotv/web-workers/dist/Orthophoto.worker.js?url";
import PanoBlurring from "@faro-lotv/web-workers/dist/PanoBlurring.worker.js?url";
import PointCloudLoading from "@faro-lotv/web-workers/dist/PointCloudLoading.worker.js?url";
import PotreeNodes from "@faro-lotv/web-workers/dist/PotreeNodes.worker.js?url";
import WSNodes from "@faro-lotv/web-workers/dist/WSNodes.worker.js?url";
import { Box, Stack, ThemeProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import {
  PropsWithChildren,
  useCallback,
  useLayoutEffect,
  type JSX,
} from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Provider as StoreProvider } from "react-redux";
import {
  BrowserRouter,
  Outlet,
  Route,
  useSearchParams,
} from "react-router-dom";
import { appTheme } from "./app-theme";
import { FeatureDetectionContextProvider } from "./components/common/feature-detection-context";
import { FeedbackContextProvider } from "./components/common/feedback/feedback-context";
import {
  ProjectAccessProvider,
  WithClientName,
} from "./components/common/project-provider/project-access-provider";
import { ProjectProvider } from "./components/common/project-provider/project-provider";
import { TiledImageSheetGenerationContextProvider } from "./components/common/tiled-image-sheet-generation-context";
import { ActiveCameraContextProvider } from "./components/r3f/cameras/active-camera-context";
import { DataManagementContextProvider } from "./components/ui/data-management/data-management-context";
import { NewFeatureProvider } from "./components/ui/new-feature/new-feature-context";
import { DataPreparationPage } from "./data-preparation-tool/data-preparation-page";
import { DataPreparationPageRedirect } from "./data-preparation-tool/data-preparation-page-redirect";
import { loginStatusCheckError } from "./errors/unrecoverable-error";
import { DataManagementPage } from "./pages/data-management-page";
import { InspectAndPublishPage } from "./registration-tools/inspect-and-publish/inspect-and-publish-page";
import { PairwiseRegistrationPage } from "./registration-tools/pairwise-registration/pairwise-registration-page";
import { setShowSpinner } from "./store/ui/ui-slice";
import { selectIntercomInfo } from "./store/user-selectors";
import { appId } from "./utils/appid";

extendR3FWithLotVComponents();

/**
 * @returns the initialization logic for the app resources (Store, logging, features)
 */
function AppInit(): null {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  useLayoutEffect(() => {
    dispatch(initFeatures(runtimeConfig));

    const featuresInUrl = parseFeaturesAndValuesFromUrl(searchParams);

    // enable features and their optional value based on the url params (url param is not case sensitive)
    for (const feature of featuresInUrl) {
      if (feature.disabled) {
        dispatch(disableFeature(feature.featureFlag));
      } else {
        dispatch(enableFeature(feature));
      }
    }
  }, [dispatch, searchParams]);
  return null;
}

/**
 * @returns a composition of all the global context providers and their initialization
 */
function AllTheProviders({ children }: PropsWithChildren): JSX.Element {
  const { externalLinks } = runtimeConfig;

  return (
    <DndProvider backend={HTML5Backend}>
      <ThemeProvider theme={appTheme}>
        {/* The CssBaseline is where the root level css is defined in the mui theme */}
        <CssBaseline />
        <DataManagementContextProvider>
          <PickersLocalizationProvider>
            <HBCookieManagerProvider
              cookieManagerUrl={externalLinks.cookieManagerLatestUrl}
            >
              <ViewRuntimeContextProvider>
                <ActiveCameraContextProvider>
                  <ToastProvider>
                    <DialogProvider>
                      <NewFeatureProvider>
                        <ErrorHandlingContext>
                          <StoreProvider store={store}>
                            <EverythingWithStoreAccess>
                              <FeatureDetectionContextProvider>
                                <WaypointContextProvider>
                                  {children}
                                </WaypointContextProvider>
                              </FeatureDetectionContextProvider>
                            </EverythingWithStoreAccess>
                          </StoreProvider>
                        </ErrorHandlingContext>
                      </NewFeatureProvider>
                    </DialogProvider>
                  </ToastProvider>
                </ActiveCameraContextProvider>
              </ViewRuntimeContextProvider>
            </HBCookieManagerProvider>
          </PickersLocalizationProvider>
        </DataManagementContextProvider>
      </ThemeProvider>
    </DndProvider>
  );
}

function EverythingWithStoreAccess({
  children,
}: PropsWithChildren): JSX.Element {
  // Initialize the logs system
  useLogging();

  const dispatch = useAppDispatch();
  const { ulmUrl } = runtimeConfig.authentication;

  const { handleErrorWithPage, handleErrorWithToast } = useErrorHandlers();

  const coreApiClient = useCoreApiClient(
    runtimeConfig.backendEndpoints.coreApiUrl,
    appId(),
  );

  const reportAccessError = useCallback(
    (error: CoreApiError) => {
      handleErrorWithPage(loginStatusCheckError(error));
    },
    [handleErrorWithPage],
  );

  const reportFailedLogout = useCallback(
    (error: unknown) => {
      handleErrorWithToast({
        title: "Failed to logout user. Please try again or reload the page",
        error,
      });
    },
    [handleErrorWithToast],
  );

  return (
    <LocalizeWrapper>
      <IntercomWrapper>
        <BrowserRouter>
          <AuthProvider
            ulmUrl={ulmUrl}
            coreApiClient={coreApiClient}
            onLogout={() => location.reload()}
            onLoginFailed={reportAccessError}
            onLogoutFailed={reportFailedLogout}
            onShowLoadingScreen={(showSpinner) =>
              dispatch(setShowSpinner(showSpinner))
            }
          >
            <AppInit />

            <HandleWindowClose />

            <FileUploadContextProvider>
              <TiledImageSheetGenerationContextProvider>
                <FeedbackContextProvider>{children}</FeedbackContextProvider>
              </TiledImageSheetGenerationContextProvider>
            </FileUploadContextProvider>
          </AuthProvider>
        </BrowserRouter>
      </IntercomWrapper>
    </LocalizeWrapper>
  );
}

/**
 * @returns the localization provider if the app has the feature enabled
 */
function LocalizeWrapper({ children }: PropsWithChildren): JSX.Element {
  const { projectKey, saveNewTranslations } =
    runtimeConfig.externalServices.localize;

  return (
    <LocalizeProvider
      projectKey={projectKey}
      initOptions={{
        saveNewPhrases: saveNewTranslations,
        enableResilientDOMMode: true,
      }}
    >
      {children}
    </LocalizeProvider>
  );
}

/**
 * @returns A context provider for the Intercom service
 */
function IntercomWrapper({ children }: PropsWithChildren): JSX.Element {
  const intercomUser = useAppSelector(selectIntercomInfo);

  return (
    <IntercomProviderWithCookieIntegration
      appId={runtimeConfig.externalServices.intercom?.appId}
      user={intercomUser}
    >
      {children}
    </IntercomProviderWithCookieIntegration>
  );
}

/**
 * @returns Route-scoped providers, which are used by all routes and that depend on
 * React Router hooks, e.g. `useLocation`, `useNavigate`, `useParams`.
 */
function RouteScopedProviders(): JSX.Element {
  return (
    <SavedViewContextProvider>
      <ProjectAccessProvider>
        <Outlet />
      </ProjectAccessProvider>
    </SavedViewContextProvider>
  );
}

/**
 * @returns Entry point of app
 */
export function App(): JSX.Element {
  /**
   * Setting the workers location to make them available to LotV during run time
   * Configuration to make them available in public, is under config-overrides.js
   */
  initLotvWorkers({
    GltfLoading,
    PanoBlurring,
    PointCloudLoading,
    PotreeNodes,
    WSNodes,
    Orthophoto,
    ImageDataLoading,
  });

  // Log app version at start
  console.log(`App version: ${APP_VERSION}`);

  return (
    <AllTheProviders>
      <Stack sx={{ height: "100%" }}>
        <Box component="div" sx={{ height: "100%" }}>
          <SentryRoutes>
            {/* Short share links resolve the saved view they name, then redirect
             * into the viewer. Deliberately outside RouteScopedProviders: the
             * link does not name a project yet, and ProjectAccessProvider
             * requires one. */}
            <Route path="/share/:shareId" element={<ShareLinkPage />} />

            {/* Legacy WebEditor links, which keep their original path when redirected here.
             * Deliberately outside RouteScopedProviders: the url does not name a project yet. */}
            <Route
              path={WEB_EDITOR_ROUTE}
              element={<WebEditorRedirectPage />}
            />

            <Route element={<RouteScopedProviders />}>
              {/* The project data loaded with ProjectProvider is commonly available to the pages
               * of the included routes. */}
              <Route element={<ProjectProvider />}>
                {/* Main Viewer UI */}
                <Route
                  path="/project/:projectId"
                  element={
                    <WithClientName clientName="sphere-web-viewer">
                      <ProjectPage />
                    </WithClientName>
                  }
                />

                {/* Main Viewer UI showing only the canvas, e.g. for embedding */}
                <Route
                  path="/project/:projectId/embed"
                  element={
                    <WithClientName clientName="sphere-web-viewer">
                      <ProjectPage canvasOnly />
                    </WithClientName>
                  }
                />

                {/* The Data Management editor. */}
                <Route
                  path="/data-management/project/:projectId"
                  element={
                    <WithClientName clientName="DataManagement">
                      <DataManagementPage />
                    </WithClientName>
                  }
                />
              </Route>

              {/* The data preparation tool, aka the staging area, aka the registration page. */}
              <Route
                path="/project/:projectId/draft"
                element={
                  <WithClientName clientName="RegistrationFrontend">
                    <DataPreparationPage />
                  </WithClientName>
                }
              />

              {/* Redirect old data preparation tool route to new draft route */}
              <Route
                path="/data-preparation/project/:projectId"
                element={
                  <WithClientName clientName="RegistrationFrontend">
                    <DataPreparationPageRedirect />
                  </WithClientName>
                }
              />

              {/* Temporary route to test the prototype of the manual cloud2cloud alignment tool.
               * Subsequent tasks will apply a better design to access the tool from the viewer. */}
              <Route
                path="/manual-alignment/project/:projectId"
                element={
                  <WithClientName clientName="RegistrationFrontend">
                    <PairwiseRegistrationPage />
                  </WithClientName>
                }
              />

              {/* Temporary route to test the multi-cloud registration.
               * Subsequent tasks will apply a better design to access the tool from the viewer. */}
              <Route
                path="/multi-registration-result/project/:projectId"
                element={
                  <WithClientName clientName="RegistrationFrontend">
                    <InspectAndPublishPage />
                  </WithClientName>
                }
              />
            </Route>

            {/* The routes below are not project scoped, so they must stay outside
             * `RouteScopedProviders`: its `ProjectAccessProvider` asserts on the `:projectId` route
             * param and would throw before these elements ever render. */}

            {/* The Viewer has no home page of its own, so the root url leads to the Dashboard.
             * Embedded components, like the Data Management error boundary, navigate to "/" when
             * they offer a "home" action, so this route has to be a working exit. */}
            <Route path="/" element={<HomeRedirect />} />

            <Route path="*" element={<NotFoundPage />} />
          </SentryRoutes>
        </Box>
      </Stack>
    </AllTheProviders>
  );
}
